// src/pages/admin/editJobs/runHelpers.js — pure helpers for manipulating assignment "runs"
// (sorting, renaming, moving per-run scoped fields) and small display-label formatters used
// by EditJobsTab. No React state; every function is a function of its arguments.

export function getCompanyUiVariant(company) {
  if (!company) return "default";
  const v = String(company.uiVariant || "").toLowerCase();
  if (v === "greenspace") return "greenspace";
  const nm = String(company.name || "").toLowerCase();
  if (nm.includes("greenspace")) return "greenspace";
  return "default";
}

export function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

export function firstNonEmpty(...values) {
  for (const v of values) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

export function getSiteContactName(site = {}) {
  return firstNonEmpty(
    site.bookingContactName,
    site.contactName,
    site.bookingContact?.name,
    site.bookingContactDetails?.name,
    site.booking?.name,
  );
}

export function getSiteContactPhone(site = {}) {
  return firstNonEmpty(
    site.bookingContactPhone,
    site.contactPhone,
    site.bookingContact?.phone,
    site.bookingContactDetails?.phone,
    site.booking?.phone,
  );
}

export function getSiteContactEmail(site = {}) {
  return firstNonEmpty(
    site.bookingContactEmail,
    site.contactEmail,
    site.bookingContact?.email,
    site.bookingContactDetails?.email,
    site.booking?.email,
  );
}

export function getTruckDisplayLabel(truck) {
  if (!truck) return "";
  const rego = String(
    truck.rego || truck.vehicleRego || truck.truckRego || "",
  ).trim();
  const loadType = String(
    truck.loadType ||
      truck.unitType ||
      truck.vehicleType ||
      truck.truckType ||
      truck.type ||
      "",
  ).trim();
  if (rego && loadType) return `${rego} • ${loadType}`;
  return rego || loadType || "";
}

export function getDriverDisplayLabel(driver) {
  if (!driver) return "";
  return driver.email || driver.displayName || driver.name || driver.uid || "Driver";
}

export function makeAssignmentDocIds(companyId, driverId, date) {
  const legacyId = `${driverId}_${date}`;
  const scopedId = companyId ? `${companyId}_${driverId}_${date}` : null;
  return { scopedId, legacyId };
}

export function getUniqueRunKey(existingRuns, preferredKey) {
  const taken = new Set(Object.keys(existingRuns || {}));
  if (!taken.has(preferredKey)) return preferredKey;

  let i = 1;
  while (taken.has(`${preferredKey} (${i})`)) i += 1;
  return `${preferredKey} (${i})`;
}

export function parseRunKey(runKey) {
  const raw = String(runKey || "").trim();

  const primary = raw.match(/^Run\s*#?\s*(\d+)$/i);
  if (primary) {
    return {
      group: 1,
      number: Number(primary[1]),
      suffix: 0,
      text: raw.toLowerCase(),
    };
  }

  const duplicate = raw.match(/^Run\s*#?\s*(\d+)\s*\((\d+)\)$/i);
  if (duplicate) {
    return {
      group: 1,
      number: Number(duplicate[1]),
      suffix: Number(duplicate[2]),
      text: raw.toLowerCase(),
    };
  }

  return {
    group: 2,
    number: Number.MAX_SAFE_INTEGER,
    suffix: 0,
    text: raw.toLowerCase(),
  };
}

export function sortRunEntries(runsObj) {
  return Object.entries(runsObj || {}).sort(([a], [b]) => {
    const pa = parseRunKey(a);
    const pb = parseRunKey(b);

    if (pa.group !== pb.group) return pa.group - pb.group;
    if (pa.number !== pb.number) return pa.number - pb.number;
    if (pa.suffix !== pb.suffix) return pa.suffix - pb.suffix;
    return pa.text.localeCompare(pb.text);
  });
}

export function sortRunsObject(runsObj) {
  const sorted = {};
  for (const [key, value] of sortRunEntries(runsObj || {})) {
    sorted[key] = value;
  }
  return sorted;
}

export function movePerRunScopedFields(sourceData, targetData, fromRunKey, toRunKey) {
  const nextSource = { ...(sourceData || {}) };
  const nextTarget = { ...(targetData || {}) };

  Object.keys(nextSource).forEach((field) => {
    if (field === "runs") return;

    const srcVal = nextSource[field];
    const tgtVal = nextTarget[field];

    if (!isPlainObject(srcVal)) return;
    if (!Object.prototype.hasOwnProperty.call(srcVal, fromRunKey)) return;

    const movedValue = srcVal[fromRunKey];

    nextSource[field] = { ...srcVal };
    delete nextSource[field][fromRunKey];

    nextTarget[field] = isPlainObject(tgtVal) ? { ...tgtVal } : {};
    nextTarget[field][toRunKey] = movedValue;
  });

  return { nextSource, nextTarget };
}

export function moveAllPerRunScopedFields(sourceData, targetData, runKeyMap) {
  let nextSource = { ...(sourceData || {}) };
  let nextTarget = { ...(targetData || {}) };

  Object.entries(runKeyMap || {}).forEach(([fromRunKey, toRunKey]) => {
    const moved = movePerRunScopedFields(
      nextSource,
      nextTarget,
      fromRunKey,
      toRunKey,
    );
    nextSource = moved.nextSource;
    nextTarget = moved.nextTarget;
  });

  return { nextSource, nextTarget };
}

export function getRunVehicleMeta(assignment, runKey, jobs = []) {
  const runMeta = assignment?.runMeta?.[runKey] || {};
  const fallbackJob =
    (jobs || []).find(
      (job) => !job?.isWeighbridgeDocket && typeof job === "object",
    ) || {};

  const unitType = firstNonEmpty(
    runMeta.unitType,
    runMeta.vehicleType,
    runMeta.truckType,
    fallbackJob.unitType,
    fallbackJob.vehicleType,
    fallbackJob.truckType,
  );

  const rego = firstNonEmpty(
    runMeta.rego,
    runMeta.vehicleRego,
    runMeta.truckRego,
    fallbackJob.rego,
    fallbackJob.vehicleRego,
    fallbackJob.truckRego,
  );

  const label = firstNonEmpty(
    runMeta.vehicleLabel,
    runMeta.vehicleName,
    runMeta.truckLabel,
    runMeta.truckName,
    fallbackJob.vehicleLabel,
    fallbackJob.vehicleName,
    fallbackJob.truckLabel,
    fallbackJob.truckName,
  );

  return {
    unitType,
    rego,
    label,
    hasVehicle: !!(unitType || rego || label),
  };
}

export function formatRunVehicle(meta) {
  if (!meta?.hasVehicle) return "No vehicle assigned";

  const primary = firstNonEmpty(meta.label, meta.unitType);
  if (primary && meta.rego) return `${primary} • ${meta.rego}`;
  return primary || meta.rego || "No vehicle assigned";
}

export function getRunCompanyId(assignment, runKey, jobs = []) {
  const runMeta = assignment?.runMeta?.[runKey] || {};
  const fallbackJob =
    (jobs || []).find(
      (job) => !job?.isWeighbridgeDocket && typeof job === "object",
    ) || {};

  const companyId = firstNonEmpty(runMeta.companyId, fallbackJob.companyId);
  if (companyId && companyId !== "mixed") return companyId;

  const rootCompanyId = firstNonEmpty(assignment?.companyId);
  if (rootCompanyId && rootCompanyId !== "mixed") return rootCompanyId;

  return "";
}

export function getNextRunKey(existingRuns) {
  const entries = sortRunEntries(existingRuns || {});
  let maxRunNumber = 0;

  entries.forEach(([runKey]) => {
    const parsed = parseRunKey(runKey);
    if (parsed.group === 1 && Number.isFinite(parsed.number)) {
      maxRunNumber = Math.max(maxRunNumber, parsed.number);
    }
  });

  return `Run #${maxRunNumber + 1}`;
}

export function removePerRunScopedFields(sourceData, runKey) {
  const nextData = { ...(sourceData || {}) };

  Object.keys(nextData).forEach((field) => {
    if (field === "runs") return;

    const value = nextData[field];
    if (!isPlainObject(value)) return;
    if (!Object.prototype.hasOwnProperty.call(value, runKey)) return;

    nextData[field] = { ...value };
    delete nextData[field][runKey];
  });

  return nextData;
}
